/**
 * Finance app localization strings -- English only for now.
 * HC-NO-HARDCODED-UI: all user-visible strings must live here.
 *
 * Finance is an EN-only app at launch. Additional locales added here
 * when translation coverage is available.
 */

/** Locales currently supported by the Finance app. EN-only at launch. */
export const SUPPORTED_LOCALES = ['en'] as const;

export const FINANCE_COPY = {
  // ── App-level navigation ──────────────────────────────────────────────────
  tabSimulation: 'Simulation',
  tabRealityTracker: 'Reality Tracker',
  tagline: 'Monte Carlo · Not financial advice',

  // ── Mobile panel navigation ───────────────────────────────────────────────
  mobileAI: 'AI',
  mobileCharts: 'Charts',
  mobileEvents: 'Events',

  // ── Simulation empty / running states ─────────────────────────────────────
  emptyStatePrompt: 'Fill in About You, then click ▶ Run or ask the AI to simulate a scenario.',
  runningState: (iterations: number) => `Running ${iterations.toLocaleString()} iterations\u2026`,

  // ── Scenario indicator ────────────────────────────────────────────────────
  scenarioLabel: 'Scenario:',

  // ── Reality page hints ────────────────────────────────────────────────────
  realityHintNoSim: 'Run a simulation first to see actuals overlaid on projections.',
  realityHintNoEntries: 'Add entries above to see them plotted against your simulation.',

  // ── TopCard: tab labels ───────────────────────────────────────────────────
  topTabAboutYou: 'About You',
  topTabSimulation: 'Simulation',
  topTabSS: 'Social Security',

  // ── TopCard: action buttons ───────────────────────────────────────────────
  btnExport: '\u2B07 Export',
  btnImport: '\u2B06 Import',
  btnShare: '\uD83D\uDD17 Share',
  btnRun: '\u25B6 Run',
  btnRunning: 'Running\u2026',
  btnRunTitle: 'Run simulation',
  btnRunIncompleteTitle: 'Fill in About You first',
  exportTitle: 'Download scenario as JSON',
  importTitle: 'Load a previously exported JSON',
  shareTitle: 'Copy share link to clipboard',

  // ── TopCard: callout ──────────────────────────────────────────────────────
  calloutDefaults: 'Values pre-filled with US national averages (2026). Edit any field to match your situation.',
  calloutShare: 'Loaded from a shared link. Edit any field to adjust.',
  calloutRemember: 'Remember my numbers in this browser',
  calloutPrivacy: 'Data stays on your device only \u2014 never sent anywhere.',
  privacyBadge: '\uD83D\uDD12 browser only',

  // ── TopCard: section labels ───────────────────────────────────────────────
  sectionIdentity: 'Identity',
  sectionIncome: 'Annual Income',
  sectionExpenses: 'Annual Expenses',
  sectionAssets: 'Current Assets',
  sectionAllocation: 'Portfolio Allocation',
  sectionRetirement: 'Retirement Target',

  // ── TopCard: field labels ─────────────────────────────────────────────────
  fieldBirthYear: 'Your birth year',
  fieldPartnerBirthYear: 'Partner birth year',
  fieldPartnerBirthYearHint: 'optional',
  fieldChildren: 'Children',
  fieldChildrenHint: 'count',
  fieldW2Income: 'Salary + bonus (W2)',
  fieldEquityComp: 'Equity / RSUs',
  fieldEquityCompHint: 'optional',
  fieldEquityYears: 'Equity over N years',
  fieldEquityYearsHint: '0=indefinite',
  fieldSelfEmployment: 'Self-employment',
  fieldSelfEmploymentHint: 'optional',
  fieldHousing: 'Housing (rent/mortgage)',
  fieldOtherExpenses: 'Other expenses',
  fieldCurrentSavings: 'Liquid savings / taxable',
  fieldRetirementBalance: 'Retirement balance (401k, IRA)',
  fieldHomeEquity: 'Home equity',
  fieldHomeEquityHint: 'optional',
  fieldEquityPct: 'Stocks',
  fieldBondsPct: 'Bonds',
  fieldCashNote: 'Cash fills the remainder',
  fieldRetirementTarget: 'Target net worth at retirement ($)',
  fieldRetirementTargetHint: (suggested: number) => `25\u00D7 expenses = $${(suggested / 1000).toFixed(0)}k suggested`,

  // ── TopCard: Social Security ──────────────────────────────────────────────
  ssYourEstimate: 'Your SS monthly benefit (est.)',
  ssPartnerEstimate: 'Partner SS monthly benefit (est.)',
  ssClaimAge: 'Claim age',
  ssClaimAgeHint: '62\u201370',
  ssPartnerClaimAge: 'Partner claim age',
  ssNote: 'Estimates based on birth year; actual benefit depends on lifetime earnings.',

  // ── ChatPanel ─────────────────────────────────────────────────────────────
  chatTitle: 'AI Scenario Builder',
  chatResetTitle: 'Clear conversation',
  chatPasswordPlaceholder: 'Password',
  chatPasswordError: 'Incorrect password. Try again.',
  chatQuickstartLabel: 'Try a scenario:',
  chatInputPlaceholder: 'Describe a scenario or ask a question\u2026',

  // ── EventsPanel ───────────────────────────────────────────────────────────
  eventsTitle: 'Life Events',
  eventsEmpty: 'No events yet.',
  eventsEmptyHint: 'Add career changes, market shocks, windfalls, or any life event that affects your finances.',
  eventsAddBtn: 'Add Event',
  eventsSelectType: 'Select event type',
  eventsYearLabel: (offset: number, startYear: number) => `Year ${offset + startYear}`,

  // ── RealityTracker ────────────────────────────────────────────────────────
  realityTitle: 'Reality Tracker',
  realityAddYearBtn: '+ Add year',
  realitySubtitle: 'Enter actual annual figures to compare against projections. Entries are append-only \u2014 use Amend only to correct errors.',
  realityEmpty: 'No entries yet. Add your first year to start tracking.',
  realityColYear: 'Year',
  realityColIncome: 'Gross Income',
  realityColExpenses: 'Expenses',
  realityColLiquid: 'Liquid',
  realityColRetirement: 'Retirement',
  realityColHomeEquity: 'Home Equity',
  realityColNetWorth: 'Net Worth',
  realityAmendBtn: 'Amend',
  realityAmendTitle: (year: number) => `Amending ${year}`,
  realityNewEntryTitle: (year: number) => `New Entry \u2014 ${year}`,
  realityFieldYear: 'Calendar year',
  realityFieldIncome: 'Gross income ($)',
  realityFieldExpenses: 'Total expenses ($)',
  realityFieldLiquid: 'Liquid assets ($)',
  realityFieldRetirement: 'Retirement assets ($)',
  realityFieldHomeEquity: 'Home equity ($)',
  realityFieldNotes: 'Notes',
  realityNotesPlaceholder: 'Optional notes for this year',
  realitySaveAmend: 'Save amendment',
  realitySaveEntry: 'Save entry',
  realityCancelBtn: 'Cancel',
} as const

export type FinanceCopy = typeof FINANCE_COPY
